<template>
  <q-page class="bg-light page-top">
    <div class="container-md">
      <!-- Заглавие -->
      <div class="header-center">
        <div class="company-title text-h6 text-mint text-bold">
          Барања од {{ companyName }}
        </div>
      </div>

      <!-- Табела -->
      <q-card class="table-card q-pa-md shadow-xs rounded-2xl">
        <div class="table-title-row">
          <div class="table-title">
            Список на внесени барања
          </div>
        </div>

        <div class="table-wrapper">
          <q-table
            :data="rowsFormatted"
            :columns="columns"
            row-key="id"
            flat
            bordered
            dense
            separator="horizontal"
            no-data-label="Немате поднесено барања."
            class="table-clean clickable-table"
            :pagination.sync="pagination"
            @row-click="selectBaranje"
          >
            <template v-slot:body-cell-datum="props">
              <q-td align="left">
                <div class="text-body2">
                  {{ props.row.datumVnesFormatted }}
                </div>
              </q-td>
            </template>

            <template v-slot:body-cell-aplikacijaNaziv="props">
              <q-td align="left">
                <div class="text-body2">
                  {{ props.row.aplikacijaNaziv }}
                </div>
              </q-td>
            </template>

            <template v-slot:body-cell-opisPreview="props">
              <q-td align="left">
                <div class="opis-preview">
                  {{ props.row.opisPreview }}
                </div>

                <q-tooltip
                  v-if="props.row.opis && props.row.opis.length > 80"
                  anchor="top middle"
                  self="bottom middle"
                >
                  Целосниот опис се прикажува во формата под табелата
                </q-tooltip>
              </q-td>
            </template>

            <template v-slot:body-cell-stepen="props">
              <q-td align="center">
                <q-badge
                  :color="getStepenColor(props.row.stepen)"
                  class="text-white"
                >
                  {{ getStepenLabel(props.row.stepen) }}
                </q-badge>
              </q-td>
            </template>

            <template v-slot:body-cell-edit="props">
              <q-td align="center">
                <q-btn
                  flat
                  round
                  color="mint"
                  icon="edit"
                  size="sm"
                  class="btn-icon"
                  @click.stop="selectBaranje(null, props.row)"
                >
                  <q-tooltip>Преглед / Уреди</q-tooltip>
                </q-btn>
              </q-td>
            </template>

            <template v-slot:body-cell-delete="props">
              <q-td align="center">
                <q-btn
                  flat
                  round
                  color="negative"
                  icon="delete"
                  size="sm"
                  class="btn-icon"
                  @click.stop="izbrisiBaranje(props.row.id)"
                >
                  <q-tooltip>Избриши</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>
        </div>
      </q-card>

      <!-- Главни копчиња -->
      <div class="footer-buttons q-mt-md q-mb-md">
        <q-btn
          label="Ново барање"
          icon="add_circle"
          class="btn-filled"
          @click="$router.push('/klient/novo-baranje')"
        />

        <q-btn
          label="Испрати"
          icon="send"
          class="btn-outline"
          :loading="loadingSave"
          @click="snimiBaranja"
        />
      </div>

      <!-- Форма за избрано барање -->
      <q-card
        v-if="editForm.id"
        class="details-card q-pa-md shadow-xs rounded-2xl q-mt-md"
      >
        <q-form @submit.prevent="zachuvajPromeni" class="edit-form">
          <div class="details-header">
            <div class="details-title">
              <q-icon name="description" class="q-mr-sm" />
              Преглед и уредување на барање
            </div>

            <q-btn
              flat
              round
              dense
              icon="close"
              color="grey-7"
              @click="clearSelection"
            >
              <q-tooltip>Затвори</q-tooltip>
            </q-btn>
          </div>

          <q-separator class="q-my-md" />

          <div class="form-grid">
            <q-input
              :value="formatDate(editForm.datumVnes)"
              label="Датум на внес"
              outlined
              dense
              color="mint"
              readonly
              class="field-equal"
            >
              <template v-slot:prepend>
                <q-icon name="event" color="mint" />
              </template>
            </q-input>

            <q-input
              v-model="editForm.baratel"
              label="Барател"
              outlined
              dense
              color="mint"
              readonly
              class="field-equal"
            >
              <template v-slot:prepend>
                <q-icon name="person" color="mint" />
              </template>
            </q-input>

            <q-select
              v-model="editForm.aplikacija"
              :options="aplikaciiOptions"
              option-value="SifraID"
              option-label="Opis"
              emit-value
              map-options
              label="Апликација"
              outlined
              dense
              color="mint"
              :loading="loadingAplikacii"
              :rules="[(v) => !!v || 'Изберете апликација']"
              class="field-equal"
            >
              <template v-slot:prepend>
                <q-icon name="apps" color="mint" />
              </template>
            </q-select>

            <q-select
              v-model="editForm.stepen"
              :options="stepenOptions"
              label="Степен на итност (1 – 5)"
              outlined
              dense
              color="mint"
              emit-value
              map-options
              :rules="[(v) => !!v || 'Изберете степен на итност']"
              class="field-equal"
            >
              <template v-slot:prepend>
                <q-icon name="whatshot" color="mint" />
              </template>
            </q-select>

            <q-select
              v-model="editForm.oddel"
              :options="oddelOptions"
              label="Оддел за поддршка"
              outlined
              dense
              color="mint"
              emit-value
              map-options
              :rules="[(v) => !!v || 'Изберете оддел за поддршка']"
              class="field-equal"
            >
              <template v-slot:prepend>
                <q-icon name="groups" color="mint" />
              </template>
            </q-select>
          </div>

         <div class="form-block">
  <q-input
    v-model="editForm.opis"
    label="Опис"
    type="textarea"
    outlined
    color="mint"
    autogrow
    class="opis-field"
    :maxlength="maxOpisLength"
    :rules="[
      (v) => !!v || 'Внесете опис',
      (v) => (v && v.trim().length <= maxOpisLength) || `Описот смее да има најмногу ${maxOpisLength} знаци`
    ]"
  >
    <template v-slot:prepend>
      <q-icon name="subject" color="mint" />
    </template>
  </q-input>

  <div class="opis-help-row">
    <div class="opis-help-text">
      Описот смее да има најмногу {{ maxOpisLength }} знаци.
    </div>

    <div
      class="opis-counter"
      :class="{ 'opis-counter-danger': editOpisLength >= maxOpisLength }"
    >
      {{ editOpisLength }}/{{ maxOpisLength }}
    </div>
  </div>
</div>

          <div class="upload-section">
            <div class="section-title">
              <q-icon name="attach_file" color="mint" size="18px" class="q-mr-xs" />
              Прикачени фајлови
            </div>

            <div
              class="white-upload q-pa-md flex flex-center column cursor-pointer"
              @click="selectFiles"
            >
              <q-icon name="cloud_upload" color="mint" size="28px" />

              <div class="upload-main-text">
                Прикачи повеќе слики / фајлови
              </div>

              <div class="upload-sub-text">
                {{
                  editForm.sliki && editForm.sliki.length
                    ? `${editForm.sliki.length} избрани фајлови`
                    : "Изберете фајлови од уредот"
                }}
              </div>

              <input
                ref="fileInput"
                type="file"
                accept="*/*"
                multiple
                class="hidden-input"
                @change="handleFileUpload"
              />
            </div>

            <div v-if="editForm.sliki && editForm.sliki.length" class="q-mt-md">
              <q-table
                :data="editForm.sliki"
                :columns="fileColumns"
                row-key="id"
                flat
                bordered
                dense
                hide-bottom
                separator="horizontal"
                class="files-table"
              >
                <template v-slot:body-cell-preview="props">
                  <q-td align="center">
                    <q-img
                      v-if="isImage(props.row.FileName)"
                      :src="getFilePreviewSrc(props.row)"
                      style="width: 42px; height: 42px; border-radius: 8px"
                      contain
                    />

                    <q-icon
                      v-else
                      name="insert_drive_file"
                      color="mint"
                      size="24px"
                    />
                  </q-td>
                </template>

                <template v-slot:body-cell-actions="props">
                  <q-td align="center">
                    <q-btn
                      flat
                      dense
                      round
                      size="sm"
                      icon="delete"
                      color="negative"
                      @click="removeFile(props.row)"
                    >
                      <q-tooltip>Отстрани</q-tooltip>
                    </q-btn>
                  </q-td>
                </template>
              </q-table>
            </div>
          </div>

          <div class="details-actions">
            <q-btn
              outline
              label="Избриши"
              color="negative"
              icon="delete"
              class="action-delete"
              @click="izbrisiBaranje(editForm.id)"
            />

            <div class="details-actions-right">
              <q-btn
                flat
                label="Откажи"
                color="grey-7"
                @click="clearSelection"
              />

              <q-btn
                label="Зачувај"
                color="mint"
                type="submit"
                icon="save"
                class="action-save"
              />
            </div>
          </div>
        </q-form>
      </q-card>
    </div>
  </q-page>
</template>

<script>
import {
  getBaranjaZaNajavenKlient,
  seedIfEmpty,
  izbrishiBaranjeSoIzvestuvanja,
} from "@/services/baranjaService";
import { getUser } from "@/services/db";
import { MoiteBaranjaFileColumns } from "@/services/forms/moiteBaranja";
import { getAplikacii } from "@/services/aplikaciiService";
import { dodadiBaranja } from "@/services/baranjaApiService";

export default {
  name: "MoiteBaranja",

  data() {
  return {
    rows: [],
    aplikaciiOptions: [],
    loadingAplikacii: false,
    loadingSave: false,
    companyName: "",
    maxOpisLength: 1000,

      editForm: {
        id: null,
        baratel: "",
        aplikacija: null,
        opis: "",
        stepen: 1,
        oddel: null,
        sliki: [],
        datumVnes: null,
      },

      stepenOptions: [
        { label: "1 - Најнизок", value: 1 },
        { label: "2 - Низок", value: 2 },
        { label: "3 - Среден", value: 3 },
        { label: "4 - Висок", value: 4 },
        { label: "5 - Најитен", value: 5 },
      ],

      oddelOptions: [
        { label: "Сервис", value: "servis" },
        { label: "Програмери", value: "programeri" },
        { label: "Менаџмент", value: "menadzment" },
      ],

      fileColumns: MoiteBaranjaFileColumns,

      pagination: {
        sortBy: "datum",
        descending: true,
        page: 1,
        rowsPerPage: 5,
      },

      columns: [
        {
          name: "baratel",
          label: "Барател",
          field: "baratel",
          align: "left",
          sortable: true,
        },
        {
          name: "datum",
          label: "Датум",
          field: "datumSort",
          align: "left",
          sortable: true,
        },
        {
          name: "aplikacijaNaziv",
          label: "Апликација",
          field: "aplikacijaNaziv",
          align: "left",
          sortable: true,
        },
        {
          name: "opisPreview",
          label: "Опис",
          field: "opisPreview",
          align: "left",
          sortable: false,
        },
        {
          name: "stepen",
          label: "Итност",
          field: "stepen",
          align: "center",
          sortable: true,
        },
        {
          name: "edit",
          label: "",
          field: "edit",
          align: "center",
        },
        {
          name: "delete",
          label: "",
          field: "delete",
          align: "center",
        },
      ],
    };
  },

 computed: {
  rowsFormatted() {
    return this.rows.map((r) => {
      const app = this.aplikaciiOptions.find((a) => a.SifraID === r.aplikacija);

      return {
        ...r,
        aplikacijaNaziv: app ? app.Opis : r.aplikacija,
        opisPreview: this.shortText(r.opis, 80),
        datumVnesFormatted: this.formatDate(r.datumVnes),
        datumSort: r.datumVnes || "",
      };
    });
  },

  editOpisLength() {
    return (this.editForm.opis || "").length;
  },
},

  methods: {
    async load() {
      seedIfEmpty();

      this.rows = getBaranjaZaNajavenKlient().map((r) => ({
        ...r,
        datumVnes: r.datumVnes || r.createdAt || r.datum || null,
      }));

      await this.loadAplikacii();
    },

    async loadAplikacii() {
      this.loadingAplikacii = true;

      try {
        this.aplikaciiOptions = await getAplikacii();
      } catch (e) {
        this.$q.notify({
          color: "negative",
          message: "Не може да се вчитаат апликациите.",
          icon: "error",
        });
      } finally {
        this.loadingAplikacii = false;
      }
    },

    formatDate(value) {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    },

    shortText(text, maxLength = 80) {
      if (!text) return "";
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    },

    getStepenColor(stepen) {
      if (stepen === 1) return "green";
      if (stepen === 2) return "teal";
      if (stepen === 3) return "orange";
      if (stepen === 4) return "deep-orange";
      if (stepen === 5) return "red";
      return "grey";
    },

    getStepenLabel(stepen) {
      const found = this.stepenOptions.find((s) => s.value === stepen);
      return found ? found.label : stepen;
    },

    getFilePreviewSrc(fileRow) {
      if (!fileRow || !fileRow.Fajl) return "";

      if (fileRow.PreviewUrl) {
        return fileRow.PreviewUrl;
      }

      if (String(fileRow.Fajl).startsWith("data:")) {
        return fileRow.Fajl;
      }

      const filename = (fileRow.FileName || "").toLowerCase();

      if (/\.(jpg|jpeg)$/i.test(filename)) {
        return `data:image/jpeg;base64,${fileRow.Fajl}`;
      }

      if (/\.(png)$/i.test(filename)) {
        return `data:image/png;base64,${fileRow.Fajl}`;
      }

      if (/\.(gif)$/i.test(filename)) {
        return `data:image/gif;base64,${fileRow.Fajl}`;
      }

      if (/\.(webp)$/i.test(filename)) {
        return `data:image/webp;base64,${fileRow.Fajl}`;
      }

      return "";
    },

    prepareFileForApi(fileRow) {
      if (!fileRow || !fileRow.Fajl) {
        return {
          Fajl: "",
          FileName: fileRow?.FileName || "",
        };
      }

      const raw = String(fileRow.Fajl).includes(",")
        ? String(fileRow.Fajl).split(",")[1]
        : String(fileRow.Fajl);

      return {
        Fajl: raw,
        FileName: fileRow.FileName || "",
      };
    },

    selectBaranje(evt, row) {
      this.editForm = {
        id: row.id,
        baratel: row.baratel || "",
        aplikacija: row.aplikacija,
        opis: row.opis || "",
        stepen: row.stepen || 1,
        oddel: row.oddel || null,
        sliki: row.sliki ? [...row.sliki] : [],
        datumVnes: row.datumVnes || null,
      };
    },

    clearSelection() {
      this.editForm = {
        id: null,
        baratel: "",
        aplikacija: null,
        opis: "",
        stepen: 1,
        oddel: null,
        sliki: [],
        datumVnes: null,
      };
    },

   zachuvajPromeni() {
  if (!this.editForm.opis || !String(this.editForm.opis).trim()) {
    this.$q.notify({
      color: "warning",
      message: "Внесете опис.",
      icon: "warning",
    });
    return;
  }

  if (String(this.editForm.opis).trim().length > this.maxOpisLength) {
    this.$q.notify({
      color: "warning",
      message: `Описот смее да има најмногу ${this.maxOpisLength} знаци.`,
      icon: "warning",
    });
    return;
  }

  const index = this.rows.findIndex((r) => r.id === this.editForm.id);

      if (index !== -1) {
        this.rows.splice(index, 1, {
          ...this.rows[index],
          ...this.editForm,
        });

        localStorage.setItem("baranja", JSON.stringify(this.rows));

        this.$q.notify({
          position: "top",
          message: "Промените се зачувани.",
          icon: "check_circle",
          color: "white",
          textColor: "mint",
          iconColor: "mint",
          timeout: 2000,
          classes: "custom-notify-success shadow-2",
        });
      }
    },

    selectFiles() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },

    async handleFileUpload(e) {
      const picked = Array.from(e.target.files || []);
      if (!picked.length) return;

      const mapped = await Promise.all(
        picked.map((file) => this.convertToBase64(file))
      );

      this.editForm.sliki = (this.editForm.sliki || []).concat(mapped);
      e.target.value = "";
    },

    convertToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const dataUrl = reader.result || "";
          const rawBase64 = String(dataUrl).includes(",")
            ? String(dataUrl).split(",")[1]
            : String(dataUrl);

          resolve({
            id: `${file.name}-${file.size}-${Date.now()}`,
            FileName: file.name,
            Fajl: rawBase64,
            PreviewUrl: dataUrl,
            sizeKB: file.size / 1024,
          });
        };

        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    removeFile(row) {
      this.editForm.sliki = this.editForm.sliki.filter((f) => f.id !== row.id);
    },

    isImage(filename) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(filename || "");
    },

    izbrisiBaranje(id) {
      this.$q
        .dialog({
          title: "Бришење",
          message: "Дали сте сигурни дека сакате да го избришете ова барање?",
          cancel: { label: "Откажи", color: "grey-7", flat: true },
          ok: { label: "Избриши", color: "negative" },
          persistent: true,
        })
        .onOk(() => {
          izbrishiBaranjeSoIzvestuvanja(id);

          this.rows = getBaranjaZaNajavenKlient().map((r) => ({
            ...r,
            datumVnes: r.datumVnes || r.createdAt || r.datum || null,
          }));

          if (this.editForm.id === id) {
            this.clearSelection();
          }

          this.$q.notify({
            position: "top",
            message: "Барањето е избришано.",
            icon: "delete_forever",
            color: "white",
            textColor: "negative",
            iconColor: "negative",
            timeout: 2000,
            classes: "custom-notify-delete shadow-2",
          });
        });
    },

    async snimiBaranja() {
      console.log("this.rows", this.rows)
      if (!this.rows.length) {
        this.$q.notify({
          color: "warning",
          message: "Немате поднесено барања.",
          icon: "warning",
        });
        return;
      }

      const user = getUser();

      if (!user || !user.id) {
        this.$q.notify({
          color: "negative",
          message: "Недостасува PartnerID. Најавете се повторно како клиент.",
          icon: "error",
        });
        return;
      }

      const invalidRow = this.rows.find(
        (r) =>
          !r.baratel ||
          !String(r.baratel).trim() ||
          !r.aplikacija ||
          !r.opis ||
          !String(r.opis).trim() ||
          !r.stepen ||
          !r.oddel
      );

      if (invalidRow) {
        this.$q.notify({
          color: "warning",
          message: "Има барање со непополнети задолжителни полиња.",
          icon: "warning",
        });
        return;
      }

      const payload = {
        PartnerID: user.id,
        List: this.rows.map((r) => ({
          Baratel: String(r.baratel || "").trim(),
          Firma: r.Firma,
          Prioritet: r.Prioritet,
          AplikacijaOpis: r.AplikacijaOpis,
          AplikacijaID: r.aplikacija,
          Opis: String(r.opis || "").trim(),
          StepenItnost: r.stepen,
          Oddel: r.oddel,
          Email: r.Email,
          DatumVnes: r.datumVnes || null,
          Files: (r.sliki || []).map((f) => this.prepareFileForApi(f)),
        })),
      };

      try {
        this.loadingSave = true;

        console.log("PAYLOAD ZA ISPRAKJANJE:", payload);

        const response = await dodadiBaranja(payload);
        console.log("ODGOVOR OD API:", response);

        this.$q.dialog({
          title: "Барањата успешно се испратени",
          ok: { label: "Во ред", color: "mint" },
        });

        this.rows = [];
        this.clearSelection();
        localStorage.removeItem("baranja");
        localStorage.removeItem("izvestuvanja");
      } catch (error) {
        console.error("GRESKA PRI ISPRAKJANJE:", error);
        console.error("SERVER RESPONSE:", error?.response?.data);

        this.$q.notify({
          color: "negative",
          message:
            error?.response?.data?.message ||
            error?.response?.data?.title ||
            "Настана грешка при испраќање на барањата.",
          icon: "error",
        });
      } finally {
        this.loadingSave = false;
      }
    },
  },

  async mounted() {
    await this.load();

    const user = getUser();

    this.companyName =
      user?.Naziv ||
      user?.naziv ||
      user?.Firma ||
      user?.firma ||
      user?.CompanyName ||
      user?.companyName ||
      "";
  },
};
</script>

<style scoped>
.bg-light {
  background-color: #f7f8fa !important;
}

.container-md {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px;
}

.text-mint {
  color: #0e6b5f !important;
}

.page-top {
  padding: 8px 24px 24px 24px;
}

.header-center {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
}

.company-title {
  width: 100%;
  text-align: center;
  position: relative;
  left: -40px;
}

.table-card,
.details-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.details-card {
  border: 1px solid #e7eeee;
}

.table-title-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.table-title {
  font-size: 15px;
  font-weight: 700;
  color: #0e6b5f;
}

.table-wrapper {
  max-height: 260px;
  overflow-y: auto;
  overflow-x: auto;
}

.table-clean thead tr {
  background-color: #f1f6f5;
  font-weight: 600;
  color: #0e6b5f;
}

.table-clean tbody tr:hover {
  background-color: #f8fbfa !important;
  transition: 0.2s ease;
}

.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.opis-preview {
  max-width: 280px;
  white-space: normal;
  line-height: 1.4;
  word-break: break-word;
  cursor: pointer;
  color: #374151;
}

.btn-icon:hover {
  transform: scale(1.15);
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.details-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #0e6b5f;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  align-items: start;
}

.form-block {
  width: 100%;
}

.field-equal {
  width: 100%;
}

.opis-field {
  width: 100%;
}
.opis-help-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.opis-help-text {
  flex: 1;
}

.opis-counter {
  font-weight: 700;
  color: #0e6b5f;
  white-space: nowrap;
}

.opis-counter-danger {
  color: #c62828;
}

.opis-field :deep(textarea) {
  min-height: 90px !important;
  max-height: 160px;
  overflow-y: auto;
}

.upload-section {
  border: 1px solid #e7ecea;
  border-radius: 14px;
  padding: 12px;
  background: #fbfcfc;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  color: #0e6b5f;
  margin-bottom: 10px;
}

.white-upload {
  border: 2px dashed #cfcfcf;
  border-radius: 14px;
  transition: all 0.2s ease;
  min-height: 85px;
  background: #ffffff;
}

.white-upload:hover {
  border-color: #53a391;
  background: #f7fdf9;
}

.upload-main-text {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: center;
}

.upload-sub-text {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}

.files-table {
  border-radius: 12px;
  overflow: hidden;
}

.hidden-input {
  display: none;
}

.details-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 4px;
  padding-top: 8px;
}

.details-actions-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.action-save {
  min-width: 130px;
  border-radius: 10px;
}

.action-delete {
  border-radius: 10px;
}

.footer-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.btn-filled {
  background-color: #0e6b5f !important;
  color: white !important;
  font-weight: 600;
  border-radius: 10px;
  height: 42px;
  padding: 0 24px;
}

.btn-outline {
  background-color: white !important;
  color: #0e6b5f !important;
  border: 1.5px solid #0e6b5f !important;
  border-radius: 10px;
  height: 42px;
  padding: 0 24px;
}

:deep(.custom-notify-success) {
  border: 1.5px solid #0e6b5f;
  border-radius: 12px;
  min-width: 320px;
  justify-content: center;
  font-weight: 600;
}

:deep(.custom-notify-delete) {
  border: 1.5px solid #c62828;
  border-radius: 12px;
  min-width: 320px;
  justify-content: center;
  font-weight: 600;
}

@media (max-width: 1024px) {
  .container-md {
    max-width: 95%;
    padding: 0 12px;
  }
}

@media (max-width: 768px) {
  .container-md {
    padding: 0 10px;
  }

  .q-page {
    padding: 10px !important;
  }

  .company-title {
    left: 0;
  }

  .table-wrapper {
    max-height: 300px;
  }

  .opis-preview {
    max-width: 180px;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .details-header {
    align-items: center;
  }

  .details-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .details-actions-right {
    width: 100%;
    flex-direction: column;
  }

  .details-actions-right .q-btn,
  .action-delete {
    width: 100%;
  }

  .footer-buttons {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .btn-filled,
  .btn-outline {
    width: 100%;
  }
}
</style>